import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useHistory } from "react-router-dom";
import { withFirebase } from "../../../Firebase";
import { getDateRange } from "../../../ViewClassroom/components/ClassroomActivity/activityUtils";
import { getLatestLogin } from "../../../../utils/getLatestLogin";
import "./index.css";

const TIME_PERIODS = [
  { value: "thisWeek", label: "This Week" },
  { value: "lastWeek", label: "Last Week" },
  { value: "thisMonth", label: "This Month" },
  { value: "lastMonth", label: "Last Month" },
  { value: "all", label: "All Time" },
];

const FILTERED_EMAILS = [
  "inaguirre96@gmail.com",
  "ignacio@stoprecidivism.org",
  "agus.llabres93@gmail.com",
];

const isFilteredEmail = (email) => {
  if (!email) return false;
  const lower = email.toLowerCase();
  return FILTERED_EMAILS.includes(lower) || lower.endsWith("@maildrop.cc");
};

const MentorTracking = ({ firebase, cachedUsers, cachedClassrooms }) => {
  const history = useHistory();
  const [timePeriod, setTimePeriod] = useState("all");
  const [mentors, setMentors] = useState([]);
  const [activities, setActivities] = useState([]);
  const [classrooms, setClassrooms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedMentor, setExpandedMentor] = useState(null);
  const [sortField, setSortField] = useState("lastActive");
  const [sortDirection, setSortDirection] = useState("desc");

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortField(field);
      setSortDirection("desc");
    }
  };

  const sortIndicator = (field) => {
    if (sortField !== field) return " \u2195";
    return sortDirection === "asc" ? " \u2191" : " \u2193";
  };

  const fetchMentorsAndClassrooms = useCallback(() => {
    if (!cachedUsers || !cachedClassrooms) return;
    const mentorList = Object.entries(cachedUsers)
      .filter(([, u]) => u.isMentor && !isFilteredEmail(u.email))
      .map(([uid, u]) => ({
        uid,
        displayName: u.displayName || u.username || "",
        email: u.email || "",
        latestLogin: getLatestLogin(u),
      }));
    setMentors(mentorList);

    const classroomList = Object.entries(cachedClassrooms).map(([id, c]) => ({
      id,
      className: c.className || "",
      teachers: c.teachers || {},
      teacherId: c.teacherId || null,
    }));
    setClassrooms(classroomList);
  }, [cachedUsers, cachedClassrooms]);

  const fetchActivities = useCallback(async () => {
    setLoading(true);
    try {
      let query = firebase.mentorActivityCollection();

      if (timePeriod !== "all") {
        const { startDate, endDate } = getDateRange(timePeriod);
        query = query
          .where("timestamp", ">=", startDate.toISOString())
          .where("timestamp", "<=", endDate.toISOString());
      }

      const snapshot = await query.get();
      const docs = snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
      setActivities(docs);
    } catch (err) {
      console.error("Error fetching mentor activity:", err);
    }
    setLoading(false);
  }, [firebase, timePeriod]);

  useEffect(() => {
    fetchMentorsAndClassrooms();
  }, [fetchMentorsAndClassrooms]);

  useEffect(() => {
    fetchActivities();
  }, [fetchActivities]);

  // Build a map of mentorUid → assigned classroom names from DB
  const assignedClassroomsMap = useMemo(() => {
    const map = {};
    classrooms.forEach((c) => {
      // Check teacherId (classroom creator)
      if (c.teacherId) {
        if (!map[c.teacherId]) map[c.teacherId] = [];
        map[c.teacherId].push(c.className);
      }
      // Check teachers array
      Object.values(c.teachers).forEach((t) => {
        if (t.uid) {
          if (!map[t.uid]) map[t.uid] = [];
          if (!map[t.uid].includes(c.className)) {
            map[t.uid].push(c.className);
          }
        }
      });
    });
    return map;
  }, [classrooms]);

  // Aggregate data per mentor
  const mentorStats = useMemo(() => {
    const statsMap = {};

    // Initialize from mentor list, using latestWebLogin as fallback
    mentors.forEach((m) => {
      statsMap[m.uid] = {
        ...m,
        logins: 0,
        notificationsSent: 0,
        outreachLogged: 0,
        participantsInvited: 0,
        instructionsDownloaded: 0,
        reportsDownloaded: 0,
        assignedClassrooms: assignedClassroomsMap[m.uid] || [],
        classroomsViewed: new Set(),
        studentsViewed: new Set(),
        tabCounts: {},
        lastActive: m.latestLogin || null,
        loginTimestamps: [],
        classroomDetails: {},
        studentDetails: {},
        notificationDetails: [],
        outreachDetails: [],
        invitationDetails: [],
        instructionDetails: [],
        reportDetails: [],
      };
    });

    activities.forEach((a) => {
      const stat = statsMap[a.mentorUid];
      if (!stat) return;

      // Track last active
      if (!stat.lastActive || a.timestamp > stat.lastActive) {
        stat.lastActive = a.timestamp;
      }

      switch (a.eventType) {
        case "login":
          stat.logins++;
          stat.loginTimestamps.push(a.timestamp);
          break;
        case "classroom_view":
          if (a.metadata?.classroomId) {
            stat.classroomsViewed.add(a.metadata.classroomId);
            const name = a.metadata.classroomName || a.metadata.classroomId;
            stat.classroomDetails[name] =
              (stat.classroomDetails[name] || 0) + 1;
          }
          break;
        case "student_view":
          if (a.metadata?.studentUid) {
            stat.studentsViewed.add(a.metadata.studentUid);
            const name = a.metadata.studentName || a.metadata.studentUid;
            stat.studentDetails[name] =
              (stat.studentDetails[name] || 0) + 1;
          }
          break;
        case "tab_view":
          if (a.metadata?.tabName) {
            stat.tabCounts[a.metadata.tabName] =
              (stat.tabCounts[a.metadata.tabName] || 0) + 1;
          }
          break;
        case "notification_sent":
          stat.notificationsSent++;
          stat.notificationDetails.push({
            type: a.metadata?.type || "unknown",
            channels: a.metadata?.channels || [],
            studentName: a.metadata?.studentName || null,
            title: a.metadata?.title || null,
            timestamp: a.timestamp,
          });
          break;
        case "outreach_logged":
          stat.outreachLogged++;
          stat.outreachDetails.push({
            channel: a.metadata?.channel || "unknown",
            studentName: a.metadata?.studentName || null,
            classroomName: a.metadata?.classroomName || "",
            timestamp: a.timestamp,
          });
          break;
        case "participant_invited":
          stat.participantsInvited += (a.metadata?.emailCount || 0) + (a.metadata?.phoneCount || 0);
          stat.invitationDetails.push({
            classroomName: a.metadata?.classroomName || "",
            emailCount: a.metadata?.emailCount || 0,
            phoneCount: a.metadata?.phoneCount || 0,
            timestamp: a.timestamp,
          });
          break;
        case "instructions_downloaded":
          stat.instructionsDownloaded++;
          stat.instructionDetails.push({
            instructionsType: a.metadata?.instructionsType || "student",
            classroomName: a.metadata?.classroomName || "",
            timestamp: a.timestamp,
          });
          break;
        case "report_downloaded":
          stat.reportsDownloaded++;
          stat.reportDetails.push({
            reportType: a.metadata?.reportType || "unknown",
            classroomName: a.metadata?.classroomName || "",
            timestamp: a.timestamp,
          });
          break;
        default:
          break;
      }
    });

    return Object.values(statsMap)
      .map((s) => ({
        ...s,
        classroomsViewedCount: s.classroomsViewed.size,
        studentsViewedCount: s.studentsViewed.size,
        topTab: Object.entries(s.tabCounts).sort((a, b) => b[1] - a[1])[0]?.[0] || "—",
        notificationDetails: s.notificationDetails.sort((a, b) => String(b.timestamp || "").localeCompare(String(a.timestamp || ""))),
        outreachDetails: s.outreachDetails.sort((a, b) => String(b.timestamp || "").localeCompare(String(a.timestamp || ""))),
        invitationDetails: s.invitationDetails.sort((a, b) => String(b.timestamp || "").localeCompare(String(a.timestamp || ""))),
        instructionDetails: s.instructionDetails.sort((a, b) => String(b.timestamp || "").localeCompare(String(a.timestamp || ""))),
        reportDetails: s.reportDetails.sort((a, b) => String(b.timestamp || "").localeCompare(String(a.timestamp || ""))),
      }))
      .sort((a, b) => {
        let aVal, bVal;
        switch (sortField) {
          case "displayName":
            aVal = (a.displayName || "").toLowerCase();
            bVal = (b.displayName || "").toLowerCase();
            return sortDirection === "asc"
              ? aVal.localeCompare(bVal)
              : bVal.localeCompare(aVal);
          case "logins":
            aVal = a.logins;
            bVal = b.logins;
            break;
          case "classroomsViewedCount":
            aVal = a.classroomsViewedCount;
            bVal = b.classroomsViewedCount;
            break;
          default:
            aVal = String(a.lastActive || "");
            bVal = String(b.lastActive || "");
            return sortDirection === "asc"
              ? aVal.localeCompare(bVal)
              : bVal.localeCompare(aVal);
        }
        return sortDirection === "asc" ? aVal - bVal : bVal - aVal;
      });
  }, [mentors, activities, assignedClassroomsMap, sortField, sortDirection]);

  const summary = useMemo(() => {
    const totalLogins = mentorStats.reduce((sum, m) => sum + m.logins, 0);
    const totalClassroomVisits = mentorStats.reduce((sum, m) => sum + m.classroomsViewedCount, 0);
    const totalStudentViews = mentorStats.reduce((sum, m) => sum + m.studentsViewedCount, 0);
    const totalNotificationsSent = mentorStats.reduce((sum, m) => sum + m.notificationsSent, 0);
    const totalOutreachLogged = mentorStats.reduce((sum, m) => sum + m.outreachLogged, 0);
    const totalParticipantsInvited = mentorStats.reduce((sum, m) => sum + m.participantsInvited, 0);
    const totalInstructionsDownloaded = mentorStats.reduce((sum, m) => sum + m.instructionsDownloaded, 0);
    const totalReportsDownloaded = mentorStats.reduce((sum, m) => sum + m.reportsDownloaded, 0);
    const activeMentors = mentorStats.filter((m) => m.lastActive).length;
    return { totalLogins, totalClassroomVisits, totalStudentViews, totalNotificationsSent, totalOutreachLogged, totalParticipantsInvited, totalInstructionsDownloaded, totalReportsDownloaded, activeMentors };
  }, [mentorStats]);

  const formatDate = (ts) => {
    if (!ts) return "—";
    try {
      return new Date(ts).toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
        hour: "numeric",
        minute: "2-digit",
      });
    } catch {
      return "—";
    }
  };

  const toggleExpand = (uid) => {
    setExpandedMentor(expandedMentor === uid ? null : uid);
  };

  return (
    <div className="mentor-tracking__container">
      <div className="mentor-tracking__filter">
        <label>Time Period:</label>
        <select
          value={timePeriod}
          onChange={(e) => setTimePeriod(e.target.value)}
        >
          {TIME_PERIODS.map((p) => (
            <option key={p.value} value={p.value}>
              {p.label}
            </option>
          ))}
        </select>
      </div>

      <div className="mentor-tracking__summary">
        <div className="mentor-tracking__card">
          <div className="mentor-tracking__card-value">{summary.totalLogins}</div>
          <div className="mentor-tracking__card-label">Total Logins</div>
        </div>
        <div className="mentor-tracking__card">
          <div className="mentor-tracking__card-value">{summary.totalClassroomVisits}</div>
          <div className="mentor-tracking__card-label">Classroom Visits</div>
        </div>
        <div className="mentor-tracking__card">
          <div className="mentor-tracking__card-value">{summary.totalStudentViews}</div>
          <div className="mentor-tracking__card-label">Student Profiles Viewed</div>
        </div>
        <div className="mentor-tracking__card">
          <div className="mentor-tracking__card-value">{summary.totalNotificationsSent}</div>
          <div className="mentor-tracking__card-label">Notifications Sent</div>
        </div>
        <div className="mentor-tracking__card">
          <div className="mentor-tracking__card-value">{summary.totalOutreachLogged}</div>
          <div className="mentor-tracking__card-label">Outreach Logged</div>
        </div>
        <div className="mentor-tracking__card">
          <div className="mentor-tracking__card-value">{summary.totalParticipantsInvited}</div>
          <div className="mentor-tracking__card-label">Participants Invited</div>
        </div>
        <div className="mentor-tracking__card">
          <div className="mentor-tracking__card-value">{summary.totalInstructionsDownloaded}</div>
          <div className="mentor-tracking__card-label">Instructions Downloaded</div>
        </div>
        <div className="mentor-tracking__card">
          <div className="mentor-tracking__card-value">{summary.totalReportsDownloaded}</div>
          <div className="mentor-tracking__card-label">Reports Downloaded</div>
        </div>
        <div className="mentor-tracking__card">
          <div className="mentor-tracking__card-value">{summary.activeMentors}</div>
          <div className="mentor-tracking__card-label">Active Coordinators</div>
        </div>
      </div>

      {loading ? (
        <div className="mentor-tracking__loading">Loading activity data...</div>
      ) : mentorStats.length === 0 ? (
        <div className="mentor-tracking__empty">No coordinators found.</div>
      ) : (
        <div className="mentor-tracking__table-container">
          <table className="mentor-tracking__table">
            <thead>
              <tr>
                <th className="mentor-tracking__sortable-th" onClick={() => handleSort("displayName")}>Coordinator Name{sortIndicator("displayName")}</th>
                <th>Email</th>
                <th className="mentor-tracking__sortable-th" onClick={() => handleSort("logins")}>Logins{sortIndicator("logins")}</th>
                <th>Assigned Classrooms</th>
                <th className="mentor-tracking__sortable-th" onClick={() => handleSort("classroomsViewedCount")}>Classroom Visits{sortIndicator("classroomsViewedCount")}</th>
                <th>Students Viewed</th>
                <th>Notifications Sent</th>
                <th>Outreach Logged</th>
                <th>Invited</th>
                <th>Instructions</th>
                <th>Reports</th>
                <th className="mentor-tracking__sortable-th" onClick={() => handleSort("lastActive")}>Last Active{sortIndicator("lastActive")}</th>
                <th>Top Tab</th>
              </tr>
            </thead>
            <tbody>
              {mentorStats.map((m) => (
                <React.Fragment key={m.uid}>
                  <tr onClick={() => toggleExpand(m.uid)}>
                    <td
                      className="mentor-tracking__name-cell"
                      onClick={(e) => { e.stopPropagation(); history.push(`/viewUser/${m.uid}`); }}
                    >
                      {m.displayName || "—"}
                    </td>
                    <td>{m.email || "—"}</td>
                    <td>{m.logins}</td>
                    <td>{m.assignedClassrooms.length}</td>
                    <td>{m.classroomsViewedCount}</td>
                    <td>{m.studentsViewedCount}</td>
                    <td>{m.notificationsSent}</td>
                    <td>{m.outreachLogged}</td>
                    <td>{m.participantsInvited}</td>
                    <td>{m.instructionsDownloaded}</td>
                    <td>{m.reportsDownloaded}</td>
                    <td>{formatDate(m.lastActive)}</td>
                    <td>{m.topTab}</td>
                  </tr>
                  {expandedMentor === m.uid && (
                    <tr className="mentor-tracking__detail-row">
                      <td colSpan={13}>
                        <div className="mentor-tracking__detail">
                          {/* Overview row: quick-glance cards */}
                          <div className="mentor-tracking__detail-overview">
                            <div className="mentor-tracking__detail-overview-card">
                              <span className="mentor-tracking__detail-overview-value">{m.assignedClassrooms.length}</span>
                              <span className="mentor-tracking__detail-overview-label">Assigned Classrooms</span>
                            </div>
                            <div className="mentor-tracking__detail-overview-card">
                              <span className="mentor-tracking__detail-overview-value">{m.logins}</span>
                              <span className="mentor-tracking__detail-overview-label">Logins</span>
                            </div>
                            <div className="mentor-tracking__detail-overview-card">
                              <span className="mentor-tracking__detail-overview-value">{m.classroomsViewedCount}</span>
                              <span className="mentor-tracking__detail-overview-label">Classroom Visits</span>
                            </div>
                            <div className="mentor-tracking__detail-overview-card">
                              <span className="mentor-tracking__detail-overview-value">{m.studentsViewedCount}</span>
                              <span className="mentor-tracking__detail-overview-label">Students Viewed</span>
                            </div>
                            <div className="mentor-tracking__detail-overview-card">
                              <span className="mentor-tracking__detail-overview-value">{m.notificationsSent}</span>
                              <span className="mentor-tracking__detail-overview-label">Notifications</span>
                            </div>
                            <div className="mentor-tracking__detail-overview-card">
                              <span className="mentor-tracking__detail-overview-value">{m.outreachLogged}</span>
                              <span className="mentor-tracking__detail-overview-label">Outreach</span>
                            </div>
                          </div>

                          {/* Detail grid */}
                          <div className="mentor-tracking__detail-grid">
                            {/* Left column: engagement data */}
                            <div className="mentor-tracking__detail-column">
                              <div className="mentor-tracking__detail-panel">
                                <h4 className="mentor-tracking__detail-panel-title">Assigned Classrooms</h4>
                                {m.assignedClassrooms.length === 0 ? (
                                  <p className="mentor-tracking__detail-empty">Not assigned to any classrooms</p>
                                ) : (
                                  <ul className="mentor-tracking__detail-list">
                                    {m.assignedClassrooms.map((name) => (
                                      <li key={name}><span className="mentor-tracking__detail-dot"></span>{name}</li>
                                    ))}
                                  </ul>
                                )}
                              </div>

                              <div className="mentor-tracking__detail-panel">
                                <h4 className="mentor-tracking__detail-panel-title">Classrooms Visited</h4>
                                {Object.keys(m.classroomDetails).length === 0 ? (
                                  <p className="mentor-tracking__detail-empty">None</p>
                                ) : (
                                  <ul className="mentor-tracking__detail-list">
                                    {Object.entries(m.classroomDetails)
                                      .sort((a, b) => b[1] - a[1])
                                      .map(([name, count]) => (
                                        <li key={name}>
                                          <span className="mentor-tracking__detail-dot"></span>
                                          {name}
                                          <span className="mentor-tracking__detail-badge">{count}</span>
                                        </li>
                                      ))}
                                  </ul>
                                )}
                              </div>

                              <div className="mentor-tracking__detail-panel">
                                <h4 className="mentor-tracking__detail-panel-title">Students Viewed</h4>
                                {Object.keys(m.studentDetails).length === 0 ? (
                                  <p className="mentor-tracking__detail-empty">None</p>
                                ) : (
                                  <ul className="mentor-tracking__detail-list">
                                    {Object.entries(m.studentDetails)
                                      .sort((a, b) => b[1] - a[1])
                                      .map(([name, count]) => (
                                        <li key={name}>
                                          <span className="mentor-tracking__detail-dot"></span>
                                          {name}
                                          <span className="mentor-tracking__detail-badge">{count}</span>
                                        </li>
                                      ))}
                                  </ul>
                                )}
                              </div>

                              <div className="mentor-tracking__detail-panel">
                                <h4 className="mentor-tracking__detail-panel-title">Tab Usage</h4>
                                {Object.keys(m.tabCounts).length === 0 ? (
                                  <p className="mentor-tracking__detail-empty">None</p>
                                ) : (
                                  <div className="mentor-tracking__detail-bars">
                                    {Object.entries(m.tabCounts)
                                      .sort((a, b) => b[1] - a[1])
                                      .map(([tab, count]) => {
                                        const max = Math.max(...Object.values(m.tabCounts));
                                        return (
                                          <div key={tab} className="mentor-tracking__detail-bar-row">
                                            <span className="mentor-tracking__detail-bar-label">{tab}</span>
                                            <div className="mentor-tracking__detail-bar-track">
                                              <div
                                                className="mentor-tracking__detail-bar-fill"
                                                style={{ width: `${(count / max) * 100}%` }}
                                              ></div>
                                            </div>
                                            <span className="mentor-tracking__detail-bar-count">{count}</span>
                                          </div>
                                        );
                                      })}
                                  </div>
                                )}
                              </div>
                            </div>

                            {/* Middle column: activity timeline */}
                            <div className="mentor-tracking__detail-column">
                              <div className="mentor-tracking__detail-panel">
                                <h4 className="mentor-tracking__detail-panel-title">Recent Logins</h4>
                                {m.loginTimestamps.length === 0 ? (
                                  <p className="mentor-tracking__detail-empty">No logins in this period</p>
                                ) : (
                                  <ul className="mentor-tracking__detail-timeline">
                                    {m.loginTimestamps
                                      .sort((a, b) => String(b || "").localeCompare(String(a || "")))
                                      .slice(0, 10)
                                      .map((ts, i) => (
                                        <li key={i} className="mentor-tracking__detail-timeline-item">
                                          <span className="mentor-tracking__detail-timeline-dot"></span>
                                          {formatDate(ts)}
                                        </li>
                                      ))}
                                  </ul>
                                )}
                              </div>

                              <div className="mentor-tracking__detail-panel">
                                <h4 className="mentor-tracking__detail-panel-title">Notifications Sent</h4>
                                {m.notificationDetails.length === 0 ? (
                                  <p className="mentor-tracking__detail-empty">None</p>
                                ) : (
                                  <ul className="mentor-tracking__detail-timeline">
                                    {m.notificationDetails.slice(0, 10).map((n, i) => (
                                      <li key={i} className="mentor-tracking__detail-timeline-item">
                                        <span className="mentor-tracking__detail-timeline-dot"></span>
                                        <div>
                                          <div className="mentor-tracking__detail-timeline-main">
                                            {n.type === "announcement"
                                              ? `Announcement: "${n.title}"`
                                              : `Message to ${n.studentName || "student"}`}
                                          </div>
                                          <div className="mentor-tracking__detail-timeline-meta">
                                            {n.channels?.length > 0 && <span className="mentor-tracking__detail-tag">{n.channels.join(", ")}</span>}
                                            {formatDate(n.timestamp)}
                                          </div>
                                        </div>
                                      </li>
                                    ))}
                                  </ul>
                                )}
                              </div>

                              <div className="mentor-tracking__detail-panel">
                                <h4 className="mentor-tracking__detail-panel-title">Outreach Logged</h4>
                                {m.outreachDetails.length === 0 ? (
                                  <p className="mentor-tracking__detail-empty">None</p>
                                ) : (
                                  <ul className="mentor-tracking__detail-timeline">
                                    {m.outreachDetails.slice(0, 10).map((o, i) => (
                                      <li key={i} className="mentor-tracking__detail-timeline-item">
                                        <span className="mentor-tracking__detail-timeline-dot"></span>
                                        <div>
                                          <div className="mentor-tracking__detail-timeline-main">
                                            <span className="mentor-tracking__detail-tag">{o.channel.replace(/_/g, " ")}</span>
                                            {o.studentName && ` ${o.studentName}`}
                                          </div>
                                          <div className="mentor-tracking__detail-timeline-meta">
                                            {o.classroomName && `${o.classroomName} · `}{formatDate(o.timestamp)}
                                          </div>
                                        </div>
                                      </li>
                                    ))}
                                  </ul>
                                )}
                              </div>
                            </div>

                            {/* Right column: downloads & invitations */}
                            <div className="mentor-tracking__detail-column">
                              <div className="mentor-tracking__detail-panel">
                                <h4 className="mentor-tracking__detail-panel-title">Participants Invited</h4>
                                {m.invitationDetails.length === 0 ? (
                                  <p className="mentor-tracking__detail-empty">None</p>
                                ) : (
                                  <ul className="mentor-tracking__detail-timeline">
                                    {m.invitationDetails.slice(0, 10).map((inv, i) => (
                                      <li key={i} className="mentor-tracking__detail-timeline-item">
                                        <span className="mentor-tracking__detail-timeline-dot"></span>
                                        <div>
                                          <div className="mentor-tracking__detail-timeline-main">{inv.classroomName}</div>
                                          <div className="mentor-tracking__detail-timeline-meta">
                                            {inv.emailCount > 0 && `${inv.emailCount} email(s)`}
                                            {inv.emailCount > 0 && inv.phoneCount > 0 && " · "}
                                            {inv.phoneCount > 0 && `${inv.phoneCount} text(s)`}
                                            {(inv.emailCount > 0 || inv.phoneCount > 0) && " · "}
                                            {formatDate(inv.timestamp)}
                                          </div>
                                        </div>
                                      </li>
                                    ))}
                                  </ul>
                                )}
                              </div>

                              <div className="mentor-tracking__detail-panel">
                                <h4 className="mentor-tracking__detail-panel-title">Instructions Downloaded</h4>
                                {m.instructionDetails.length === 0 ? (
                                  <p className="mentor-tracking__detail-empty">None</p>
                                ) : (
                                  <ul className="mentor-tracking__detail-timeline">
                                    {m.instructionDetails.slice(0, 10).map((inst, i) => (
                                      <li key={i} className="mentor-tracking__detail-timeline-item">
                                        <span className="mentor-tracking__detail-timeline-dot"></span>
                                        <div>
                                          <div className="mentor-tracking__detail-timeline-main">
                                            {inst.instructionsType === "mentor" ? "Coordinator" : "Student"} instructions
                                          </div>
                                          <div className="mentor-tracking__detail-timeline-meta">
                                            {inst.classroomName && `${inst.classroomName} · `}{formatDate(inst.timestamp)}
                                          </div>
                                        </div>
                                      </li>
                                    ))}
                                  </ul>
                                )}
                              </div>

                              <div className="mentor-tracking__detail-panel">
                                <h4 className="mentor-tracking__detail-panel-title">Reports Downloaded</h4>
                                {m.reportDetails.length === 0 ? (
                                  <p className="mentor-tracking__detail-empty">None</p>
                                ) : (
                                  <ul className="mentor-tracking__detail-timeline">
                                    {m.reportDetails.slice(0, 10).map((r, i) => (
                                      <li key={i} className="mentor-tracking__detail-timeline-item">
                                        <span className="mentor-tracking__detail-timeline-dot"></span>
                                        <div>
                                          <div className="mentor-tracking__detail-timeline-main">
                                            {r.reportType.replace(/_/g, " ")}
                                          </div>
                                          <div className="mentor-tracking__detail-timeline-meta">
                                            {r.classroomName && `${r.classroomName} · `}{formatDate(r.timestamp)}
                                          </div>
                                        </div>
                                      </li>
                                    ))}
                                  </ul>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default withFirebase(MentorTracking);
